import { useEffect, useRef, type ReactNode } from "react";
import { cn } from "../lib/cn.js";
import { Empty } from "./Empty.js";
import { InlineLoading } from "./Spinner.js";

export interface ColumnDef<T> {
  header: ReactNode;
  /** 列头宽度 className（e.g. "w-32" / "w-24"） */
  width?: string;
  /** 渲染单元格。可读 row、index。返 ReactNode。 */
  render: (row: T, index: number) => ReactNode;
  /** 单元格 className（默认 left-aligned）。 */
  cellClassName?: string;
}

/**
 * Phase C.2 共用 Table。dosia-bg-light 表头，border 分隔；emerald/amber 行高亮交给 cellClassName。
 * 可选 checkbox 多选列（selectable）。
 */
export function Table<T>({
  columns,
  rows,
  rowKey,
  loading,
  empty,
  className,
  onRowClick,
  selectable,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}: {
  columns: ReadonlyArray<ColumnDef<T>>;
  rows: ReadonlyArray<T>;
  rowKey: (row: T, index: number) => string;
  loading?: boolean;
  empty?: ReactNode;
  className?: string;
  onRowClick?: (row: T) => void;
  selectable?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (rowKey: string) => void;
  onToggleSelectAll?: () => void;
}): JSX.Element {
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const allSelected =
    selectable === true &&
    rows.length > 0 &&
    rows.every((r, i) => selectedIds?.has(rowKey(r, i)));
  const someSelected =
    selectable === true &&
    rows.some((r, i) => selectedIds?.has(rowKey(r, i)));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  if (loading) return <InlineLoading />;
  if (!rows.length) {
    return empty ? <>{empty}</> : <Empty title="暂无数据" />;
  }
  return (
    <div
      className={cn(
        "w-full border border-dosia-border rounded-md overflow-hidden bg-dosia-bg-light",
        className,
      )}
    >
      <table className="w-full text-sm text-left">
        <thead className="bg-dosia-bg-lighter text-dosia-text-secondary text-xs">
          <tr>
            {selectable ? (
              <th className="w-8 px-2 py-2 font-medium border-b border-dosia-border">
                <input
                  ref={headerCheckboxRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={() => onToggleSelectAll?.()}
                  aria-label="全选"
                />
              </th>
            ) : null}
            {columns.map((col, i) => (
              <th
                key={i}
                className={cn(
                  "px-3 py-2 font-medium border-b border-dosia-border",
                  col.width,
                )}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={rowKey(row, i)}
              className={cn(
                "border-b border-dosia-border-light last:border-b-0 text-dosia-text",
                onRowClick && "cursor-pointer hover:bg-dosia-hover",
              )}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {selectable ? (
                <td className="w-8 px-2 py-2 align-middle">
                  <input
                    type="checkbox"
                    checked={selectedIds?.has(rowKey(row, i)) ?? false}
                    onChange={() => onToggleSelect?.(rowKey(row, i))}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`选择 ${rowKey(row, i)}`}
                  />
                </td>
              ) : null}
              {columns.map((col, j) => (
                <td
                  key={j}
                  className={cn("px-3 py-2 align-middle", col.cellClassName)}
                >
                  {col.render(row, i)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}